import { memo } from "react";
import { Eye } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { formatCurrency } from "@/shared/lib/formatters";
import { CommissionTypeBadge } from "./CommissionTypeBadge";
import type { CommissionSummaryDTO } from "../types/commission.types";

interface CommissionSummaryTableProps {
  summaries: CommissionSummaryDTO[];
  loading: boolean;
  onOpenDetails: (summary: CommissionSummaryDTO) => void;
}

/**
 * Tabela agregada de comissões — uma linha por vendedor com totais
 * do período. Mostra breakdown por tipo em chips dentro da célula.
 *
 * O endpoint `/summary` não é paginado — todas as linhas são
 * renderizadas. O botão "Detalhes" abre a modal com as comissões
 * individuais daquele vendedor.
 *
 * @example
 * <CommissionSummaryTable
 *   summaries={summary.data}
 *   loading={summary.loading}
 *   onOpenDetails={(seller) => setSelectedSeller(seller)}
 * />
 */
export const CommissionSummaryTable = memo(function CommissionSummaryTable({
  summaries,
  loading,
  onOpenDetails,
}: CommissionSummaryTableProps) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Vendedor</TableHead>
            <TableHead className="text-right">Total</TableHead>
            <TableHead className="text-center">Origens</TableHead>
            <TableHead>Detalhamento</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {summaries.map((summary) => (
            <TableRow key={summary.sellerId}>
              <TableCell className="font-medium">
                {summary.sellerName}
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatCurrency(summary.totalCommission)}
              </TableCell>
              <TableCell className="text-center text-muted-foreground">
                {summary.rentalCount}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1.5">
                  {summary.commissionsByType.map((breakdown) => (
                    <span
                      key={breakdown.type}
                      className="inline-flex items-center gap-1.5"
                      title={`${breakdown.count} ${breakdown.count === 1 ? "comissão" : "comissões"} · ${formatCurrency(breakdown.value)}`}
                    >
                      <CommissionTypeBadge type={breakdown.type} />
                      <span className="text-xs text-muted-foreground">
                        {breakdown.count} · {formatCurrency(breakdown.value)}
                      </span>
                    </span>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <Can permission={ACTIONS.commissions.read}>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onOpenDetails(summary)}
                    title="Ver detalhes"
                    aria-label={`Ver detalhes das comissões de ${summary.sellerName}`}
                    disabled={loading}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </Can>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
});
